import { createPortal } from "react-dom";
import { X, Plus } from "lucide-react";

export const MAX_COMPARE = 4;

export const CompareBar = ({ shades, qualityName, onRemove, onClear, onAdd, isPinned, hidden }) => {
  if (!shades.length || hidden) return null;

  const slots = [...shades, ...Array(Math.max(0, MAX_COMPARE - shades.length)).fill(null)];

  return createPortal(
    <section
      data-testid="compare-bar"
      aria-label="Shade comparison"
      className="fixed z-[45] inset-x-0 bottom-[52px] lg:bottom-6 lg:left-6 lg:right-auto lg:w-[420px] border-t lg:border lg:rounded-sm"
      style={{
        backgroundColor: "var(--mp-card)",
        borderColor: "var(--mp-line)",
        boxShadow: "0 -12px 40px rgba(16,16,16,0.22)",
      }}
    >
      <div
        className="flex items-center justify-between px-4 py-2.5 border-b"
        style={{ borderColor: "var(--mp-line)" }}
      >
        <p
          className="text-[10px] tracking-[0.18em] uppercase"
          style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
        >
          Comparing {shades.length} of {MAX_COMPARE}
        </p>
        <button
          onClick={onClear}
          data-testid="compare-clear"
          className="text-[11px] transition-opacity hover:opacity-60"
          style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
        >
          Clear
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2 px-4 py-3 max-w-[420px]">
        {slots.map((s, idx) =>
          s ? (
            <div key={s.number} data-testid={`compare-slot-${s.number}`}>
              <div
                className="relative aspect-square rounded-sm overflow-hidden border"
                style={{ backgroundColor: s.hex, borderColor: "var(--mp-line)" }}
              >
                {s.image_url && (
                  <img
                    src={s.image_url}
                    alt={`Shade ${s.number}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
                <button
                  onClick={() => onRemove(s.number)}
                  data-testid={`compare-remove-${s.number}`}
                  aria-label={`Remove shade ${s.number} from comparison`}
                  className="absolute top-1 right-1 p-0.5 rounded-full"
                  style={{ backgroundColor: "rgba(16,16,16,0.6)", color: "#FFFFFF" }}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <p
                className="mt-1 text-[11px] text-center font-semibold tabular-nums"
                style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
              >
                #{s.number}
              </p>
              <button
                onClick={() => onAdd(s)}
                data-testid={`compare-add-${s.number}`}
                className="mt-1 w-full text-[10px] py-1 rounded-sm border transition-opacity hover:opacity-70"
                style={{
                  borderColor: isPinned(s.number) ? "var(--mp-copper)" : "var(--mp-line)",
                  color: isPinned(s.number) ? "var(--mp-copper)" : "var(--mp-muted)",
                  fontFamily: "'Manrope', sans-serif",
                }}
              >
                {isPinned(s.number) ? "In order" : "Add"}
              </button>
            </div>
          ) : (
            <div
              key={`empty-${idx}`}
              className="aspect-square rounded-sm border border-dashed flex items-center justify-center"
              style={{ borderColor: "var(--mp-line)" }}
              data-testid={`compare-slot-empty-${idx}`}
            >
              <Plus className="w-4 h-4" style={{ color: "var(--mp-line)" }} />
            </div>
          )
        )}
      </div>
      <p
        className="px-4 pb-3 text-[11px]"
        style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
      >
        {qualityName} · hold up to {MAX_COMPARE} shades side by side
      </p>
    </section>,
    document.body
  );
};
